export type OwnMapViewMode = 'flat' | 'globe';

export interface OwnMapTeam {
  id: number;
  name: string;
  short: string;
  /** Fill color for the map (normalized #RRGGBB). */
  color: string;
  espn_team_id?: number | null;
  logo_url?: string | null;
  /** ESPN / DB conference short name (e.g. "Big Ten"). */
  conference?: string | null;
}

/** Parse a color string to lowercase #rrggbb, or null if unusable. */
export function parseTeamHex(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith('rgb')) {
    const m = s.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (!m) return null;
    const hex = [m[1], m[2], m[3]]
      .map((n) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, '0'))
      .join('');
    return `#${hex}`;
  }
  if (s[0] !== '#') s = `#${s}`;
  if (/^#([0-9a-fA-F]{3})$/.test(s)) {
    const [, h] = s.match(/^#([0-9a-fA-F]{3})$/)!;
    s = `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(s)) return null;
  return s.toLowerCase();
}

export function isNearBlackHex(hex: string | null | undefined): boolean {
  const h = parseTeamHex(hex);
  if (!h) return false;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return r + g + b < 45;
}

/**
 * Map fill from brand colors. Prefer map_color (kept in sync with primary),
 * then primary; near-black → secondary so territories stay readable on the dark stage.
 */
export function pickMapFillColor(opts: {
  mapColor?: string | null;
  primary?: string | null;
  secondary?: string | null;
}): string {
  const map = parseTeamHex(opts.mapColor);
  const primary = parseTeamHex(opts.primary);
  const secondary = parseTeamHex(opts.secondary);
  const preferred = map || primary;
  if (preferred && isNearBlackHex(preferred)) {
    if (secondary && !isNearBlackHex(secondary)) return secondary;
    return '#4a5568';
  }
  if (preferred) return preferred;
  if (secondary && !isNearBlackHex(secondary)) return secondary;
  return '#6b7280';
}

/** Ensure API colors are canvas-safe #RRGGBB (fallback gray). Near-black → gray. */
export function normalizeTeamColor(...candidates: Array<string | null | undefined>): string {
  for (const raw of candidates) {
    const hex = parseTeamHex(raw);
    if (!hex) continue;
    if (isNearBlackHex(hex)) return '#4a5568';
    return hex;
  }
  return '#6b7280';
}

/** CSS `r, g, b` triple for `rgba(var(--x), a)` usage. */
export function teamColorRgbChannels(color: string | null | undefined): string {
  const hex = normalizeTeamColor(color);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

/** FIPS → team_id plus team lookup for the canvas renderer. */
export interface OwnMapOwnership {
  ownership: Record<string, number>;
  teams: Record<string, OwnMapTeam>;
  snapshotDate: string | null;
}

/** @deprecated Use OwnMapSelection */
export interface OwnMapCountyPick {
  fips: string;
  name: string;
  stateAbbr: string;
  team: OwnMapTeam;
}

/** Two-step map selection: territory first, then county. */
export type OwnMapSelection =
  | {
      mode: 'territory';
      team: OwnMapTeam;
      countyCount: number;
    }
  | {
      mode: 'county';
      fips: string;
      name: string;
      stateAbbr: string;
      team: OwnMapTeam;
      countyCount: number;
    };
